
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  FreeLuggage,
  InputArea,
  InputHeader,
  PaidLuggage,
  SingleInput,
  SpecialLuggageWrap,
} from "./SeatCount.styles.js";

const SeatCount = ({
  totalSelectSeat = 0,
  luggageSettings,
  setNewLuggageData,
  newLuggageData = {},
  setSpecialLuggageName,
  specialLuggageName = "",
}) => {
  const { webSettingData, languageData } = useSelector(
    (state) => state.busLists
  );

  const [showSpecialLuggage, setShowSpecialLuggage] = useState(true);

  const language = webSettingData?.language;

  const text = (key, fallback) =>
    languageData?.[key]?.[language] || fallback;

  useEffect(() => {
    if (!setNewLuggageData) return;

    // Keep luggage quantities valid when settings or limits change.
    setNewLuggageData((previous) => {
      const next = { ...previous };

      const paidLimit = Math.max(
        0,
        Number(luggageSettings?.paid_max_luggage_pcs) || 0
      );

      const specialLimit = Math.max(
        0,
        Number(luggageSettings?.special_max_luggage_pcs) || 0
      );

      const paidQuantity = Number(next.paid_max_luggage_pcs) || 0;
      const specialQuantity = Number(next.paid_max_luggage_kg) || 0;

      next.paid_max_luggage_pcs = Math.min(
        Math.max(0, paidQuantity),
        paidLimit
      );

      next.paid_max_luggage_kg = Math.min(
        Math.max(0, specialQuantity),
        specialLimit
      );

      return next;
    });
  }, [
    luggageSettings?.paid_max_luggage_pcs,
    luggageSettings?.special_max_luggage_pcs,
    setNewLuggageData,
  ]);

  const handleLuggage = (event, configuredLimit) => {
    const { id, value } = event.target;

    const limits = {
      paid_max_luggage_pcs: Number(
        luggageSettings?.paid_max_luggage_pcs
      ),
      paid_max_luggage_kg: Number(
        luggageSettings?.special_max_luggage_pcs
      ),
    };

    const limit = Math.max(
      0,
      Number.isFinite(Number(configuredLimit))
        ? Number(configuredLimit)
        : limits[id] || 0
    );

    // Allow an empty field while the user edits the quantity.
    if (value === "") {
      setNewLuggageData((previous) => ({
        ...previous,
        [id]: "",
      }));
      return;
    }

    const quantity = Number(value);

    if (!Number.isFinite(quantity) || quantity < 0) {
      toast.error(text("invalid_luggage_quantity", "Enter a valid quantity."));
      return;
    }

    if (!Number.isInteger(quantity)) {
      toast.error(text("whole_luggage_quantity", "Enter a whole number."));
      return;
    }

    if (quantity > limit) {
      toast.error(
        `${text("you_cant_take_more_than", "You cannot take more than")} ${limit}`
      );

      setNewLuggageData((previous) => ({
        ...previous,
        [id]: limit,
      }));
      return;
    }

    setNewLuggageData((previous) => ({
      ...previous,
      [id]: quantity,
    }));
  };

  return (
    <>
      {webSettingData?.luggage_service && (
        <div style={{ color: "#E5343D" }}>
          <p>
            *** {text("one_luggage", "One luggage item")}{" "}
            <strong>{luggageSettings?.free_luggage_kg ?? 0}</strong>{" "}
            {text("kg_is_free", "kg is free")}.
          </p>

          <p>
            *** {text("special_luggage", "Special luggage")} (
            {text("max_length", "Max length")}{" "}
            <strong>{luggageSettings?.max_length ?? 0}</strong>{" "}
            {text("meters", "meters")} {text("and", "and")}{" "}
            {text("max_weight", "max weight")}{" "}
            <strong>{luggageSettings?.max_weight ?? 0}</strong> kg)
          </p>
        </div>
      )}

      <InputArea>

        {webSettingData?.luggage_service && (
          <>
            <PaidLuggage>
              <InputHeader htmlFor="paid_max_luggage_pcs">
                {text("paid_luggage", "Paid luggage")} (
                {text("maximum", "Max")}{" "}
                {luggageSettings?.paid_max_luggage_pcs ?? 0} Pcs) |{" "}
                {text("price_per_pcs", "Price per piece")} (
                {luggageSettings?.price_pcs ?? 0})
              </InputHeader>

              <SingleInput
                id="paid_max_luggage_pcs"
                name="paid_max_luggage_pcs"
                type="number"
                min="0"
                max={Number(luggageSettings?.paid_max_luggage_pcs) || 0}
                step="1"
                value={newLuggageData?.paid_max_luggage_pcs ?? ""}
                onChange={(event) =>
                  handleLuggage(
                    event,
                    luggageSettings?.paid_max_luggage_pcs
                  )
                }
                placeholder="0"
              />
            </PaidLuggage>

            <PaidLuggage>
              <InputHeader htmlFor="paid_max_luggage_kg">
                {text("special_luggage", "Special luggage")} (
                {text("maximum", "Max")}{" "}
                {luggageSettings?.special_max_luggage_pcs ?? 0} Pcs) |{" "}
                {text("price_per_pcs", "Price per piece")} (
                {luggageSettings?.special_price_pcs ?? 0})
              </InputHeader>

              <SingleInput
                id="paid_max_luggage_kg"
                name="paid_max_luggage_kg"
                type="number"
                min="0"
                max={Number(luggageSettings?.special_max_luggage_pcs) || 0}
                step="1"
                value={newLuggageData?.paid_max_luggage_kg ?? ""}
                onChange={(event) =>
                  handleLuggage(
                    event,
                    luggageSettings?.special_max_luggage_pcs
                  )
                }
                placeholder="0"
              />
            </PaidLuggage>

            <SpecialLuggageWrap>
              <InputHeader htmlFor="specialLuggage">
                <label
                  htmlFor="specialLuggage"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <input
                    id="specialLuggage"
                    type="checkbox"
                    checked={showSpecialLuggage}
                    onChange={(event) =>
                      setShowSpecialLuggage(event.target.checked)
                    }
                  />
                  {text("special_luggage", "Special luggage")}
                </label>
              </InputHeader>

              {showSpecialLuggage && (
                <SingleInput
                  id="specialLuggageName"
                  name="specialLuggageName"
                  type="text"
                  value={specialLuggageName || ""}
                  onChange={(event) =>
                    setSpecialLuggageName?.(event.target.value)
                  }
                  placeholder={text(
                    "item_name_here",
                    "Enter item name"
                  )}
                />
              )}
            </SpecialLuggageWrap>
          </>
        )}
      </InputArea>
    </>
  );
};

export default SeatCount;
